import { api } from '../../../../lib/api';
import type { BusinessProfile, BusinessProfileInput, PromotionInput, PromotionRow } from '../../../../types/businessProfile';
import type { PortalSetupResponse, PortalSpeedToLeadView } from '../../../../types/kyckConnect';
import type { KyckConnectServices } from '../../../../lib/kyckConnect/services';
import type { KyckConnectDashboard, DashboardDays, KyckConnectReport, ReportMonths } from '../../../../types/kyckConnectInsights';
import type { Grouping } from '../../../../lib/kyckConnect/metrics';
import type { SpeedToLeadStaffEntry } from '../../../../types/speedToLead';

// THE CLUB'S SETUP API (KyckConnect Phase 4). ⚠️ Everything here hits /api/portal/kyckconnect behind requirePortalAuth. The server
// resolves the scope from the session; a club id only ever selects WITHIN it (outside it is a 404), and every write needs the
// `kyckconnect_setup` permission (403 without). This file never imports a staff api (a source guard pins it).

const BASE = '/api/portal/kyckconnect';
const club = (accountId: string) => `${BASE}/clubs/${encodeURIComponent(accountId)}`;

export interface PortalKcClubs {
  clubs: Array<{ accountId: string; name: string; services: KyckConnectServices }>;
  canEdit: boolean;
}
export const fetchPortalKcClubs = () => api<PortalKcClubs>(`${BASE}/clubs`);
export const fetchPortalSetup = (accountId: string) => api<PortalSetupResponse>(`${club(accountId)}/setup`);

export interface PortalProfileBundle {
  accountName: string | null;
  profile: BusinessProfile | null;
  promotions: PromotionRow[];
  timezone: string;
}
export const fetchPortalProfile = (accountId: string) => api<PortalProfileBundle>(`${club(accountId)}/profile`);
export const savePortalProfile = (accountId: string, input: BusinessProfileInput) =>
  api<BusinessProfile>(`${club(accountId)}/profile`, { method: 'PUT', body: JSON.stringify(input) });
export const createPortalPromotion = (accountId: string, input: PromotionInput) =>
  api<PromotionRow>(`${club(accountId)}/promotions`, { method: 'POST', body: JSON.stringify(input) });
export const updatePortalPromotion = (accountId: string, id: string, input: PromotionInput) =>
  api<PromotionRow>(`${club(accountId)}/promotions/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) });
export const deletePortalPromotion = (accountId: string, id: string) =>
  api<{ ok: true }>(`${club(accountId)}/promotions/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const fetchPortalSpeedToLeadView = (accountId: string) => api<PortalSpeedToLeadView>(`${club(accountId)}/speed-to-lead`);
export interface PortalSpeedToLeadBody {
  staffList: SpeedToLeadStaffEntry[];
  staffRingSeconds: number;
  roundTwoDelaySeconds: number;
  retryAfterMinutes: number | null;
  messages?: { whisperMessage: string | null; missedCallMessage: string | null; outboundWhisperMessage: string | null };
}
export const savePortalSpeedToLead = (accountId: string, body: PortalSpeedToLeadBody) =>
  api<PortalSpeedToLeadView>(`${club(accountId)}/speed-to-lead`, { method: 'PUT', body: JSON.stringify(body) });

export const fetchPortalDashboard = (days: DashboardDays, accountId: string | null) =>
  api<KyckConnectDashboard>(`${BASE}/dashboard?days=${days}${accountId ? `&club=${encodeURIComponent(accountId)}` : ''}`);

export const fetchPortalReport = (f: { club: string | null; months: ReportMonths; grouping: Grouping; compare: boolean }) =>
  api<KyckConnectReport>(`${BASE}/report?months=${f.months}&grouping=${f.grouping}&compare=${f.compare ? 1 : 0}${f.club ? `&club=${encodeURIComponent(f.club)}` : ''}`);

// The club's own assistant (club onboarding). ⚠️ No reply-mode function exists here: only a Kyck admin changes it.
export interface PortalAssistantBody {
  assistantName?: string;
  capabilities?: { discussPromotions: boolean; bookAppointments: boolean; answerFaqs: boolean; handleObjections: boolean };
  followUps?: { noResponse: Array<{ enabled: boolean; delayMinutes: number; message: string }>; incomplete: Array<{ enabled: boolean; delayMinutes: number; message: string }> };
  handoff?: { name: string; phone: string; email: string };
  testNumbers?: string[];
}
export const savePortalAssistant = (accountId: string, body: PortalAssistantBody) =>
  api<PortalSetupResponse>(`${club(accountId)}/assistant`, { method: 'PUT', body: JSON.stringify(body) });
export const changePortalLaunch = (accountId: string, to: 'setting_up' | 'testing' | 'live' | 'on_hold') =>
  api<{ launchStatus: string | null }>(`${club(accountId)}/launch`, { method: 'POST', body: JSON.stringify({ to }) });
export const confirmPortalCopied = (accountId: string, body: { sections?: string[]; promotionIds?: string[]; followUps?: boolean }) =>
  api<{ sectionsCleared: number; promotionsCleared: number; followUpsCleared: boolean }>(`${club(accountId)}/confirm-copied`, { method: 'POST', body: JSON.stringify(body) });
